// src/pages/ClientConfigurationPage.jsx - MANUAL FIREBASE AUTH SETUP
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getClientById,
  updateClientConfiguration,
  addAdminToClient,
  updateAdminUser,
  deleteAdminUser,
  getClientModules,
  updateClientModules
} from '../api/superAdminService';
import { DEFAULT_PERMISSIONS } from '../api/salonAuthService';
import '../styles/superadmin.css';

function ClientConfigurationPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('modules');
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Modal states
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [showEditAdminModal, setShowEditAdminModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  // Modules state
  const [modules, setModules] = useState({
    home: true,
    promotions: false,
    communications: false,
    bookings: false,
    reports: false,
    bespokeai: false,
    bookingsAI: false,
    communicationsAI: false,
    loyaltyAI: false,
    reportsAI: false
  });
  const [loadingModules, setLoadingModules] = useState(false);

  useEffect(() => {
    loadClientData();
  }, [clientId]);

  const loadClientData = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getClientById(clientId);

      setClientData(data);

      if (data.enabledModules) {
        const modulesToSet = {
          home: true,
          promotions: data.enabledModules.promotions || false,
          communications: data.enabledModules.communications || false,
          bookings: data.enabledModules.bookings || false,
          reports: data.enabledModules.reports || false,
          bespokeai: data.enabledModules.bespokeai || false,
          bookingsAI: data.enabledModules.bookingsAI || false,
          communicationsAI: data.enabledModules.communicationsAI || false,
          loyaltyAI: data.enabledModules.loyaltyAI || false,
          reportsAI: data.enabledModules.reportsAI || false
        };

        setModules(modulesToSet);
        setLoadingModules(false);
      } else {
        await loadModules(clientId);
      }
    } catch (err) {
      console.error('Error loading client data:', err);
      setError('Failed to load client configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadModules = async (clientId) => {
    try {
      setLoadingModules(true);
      const clientModules = await getClientModules(clientId);
      if (clientModules) {
        setModules({
          home: true,
          promotions: clientModules.promotions || false,
          communications: clientModules.communications || false,
          bookings: clientModules.bookings || false,
          reports: clientModules.reports || false,
          bespokeai: clientModules.bespokeai || false,
          bookingsAI: clientModules.bookingsAI || false,
          communicationsAI: clientModules.communicationsAI || false,
          loyaltyAI: clientModules.loyaltyAI || false,
          reportsAI: clientModules.reportsAI || false
        });
      }
    } catch (err) {
      console.error('Error loading modules:', err);
      setError('Failed to load module configuration');
    } finally {
      setLoadingModules(false);
    }
  };

  const handleBackToClients = () => {
    navigate('/superadmin/dashboard', { state: { activeTab: 'clients' } });
  };

  const handleAddAdmin = async (adminData) => {
    try {
      setSaving(true);
      setError('');
      
      console.log('=== START: handleAddAdmin ===');
      console.log('Client ID:', clientId);
      console.log('Admin data:', adminData);
      console.log('Modules:', modules);
      
      const result = await addAdminToClient(clientId, adminData, modules);
      
      console.log('Admin creation result:', result);
      
      setShowAddAdminModal(false);
      setSuccessMessage('Admin user added successfully to salonUsers collection!');
      
      await loadClientData();
      setTimeout(() => setSuccessMessage(''), 5000);
      
      console.log('=== END: handleAddAdmin - SUCCESS ===');
    } catch (err) {
      console.error('Error adding admin:', err);
      setError('Failed to add admin user: ' + err.message);
      console.log('=== END: handleAddAdmin - ERROR ===');
    } finally {
      setSaving(false);
    }
  };

  const handleEditAdmin = async (adminData) => {
    try {
      setSaving(true);
      setError('');
      await updateAdminUser(selectedAdmin.uid, adminData);
      setShowEditAdminModal(false);
      setSelectedAdmin(null);
      setSuccessMessage('Admin user updated successfully');
      await loadClientData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating admin:', err);
      setError('Failed to update admin user: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAdmin = async (adminUid, adminName) => {
    if (!window.confirm(`Are you sure you want to delete admin user "${adminName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      await deleteAdminUser(adminUid);
      setSuccessMessage('Admin user deleted successfully');
      await loadClientData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting admin:', err);
      setError('Failed to delete admin user: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Maps each parent module to the AI sub-modules that depend on it
  const AI_PARENT_DEPS = {
    bookings: 'bookingsAI',
    communications: 'communicationsAI',
    promotions: 'loyaltyAI',
    reports: 'reportsAI'
  };

  const handleModuleToggle = (moduleName) => {
    if (moduleName === 'home') return;

    setModules(prev => {
      const next = { ...prev, [moduleName]: !prev[moduleName] };
      // If a parent module is being disabled, also disable its dependent AI sub-module
      if (prev[moduleName] && AI_PARENT_DEPS[moduleName]) {
        next[AI_PARENT_DEPS[moduleName]] = false;
      }
      // If BespokeAI itself is being disabled, clear all AI sub-modules
      if (moduleName === 'bespokeai' && prev.bespokeai) {
        next.bookingsAI = false;
        next.communicationsAI = false;
        next.loyaltyAI = false;
        next.reportsAI = false;
      }
      return next;
    });
  };

  // Maps each AI sub-module to the parent module it requires
  const AI_SUBMODULE_CONFIG = [
    {
      key: 'bookingsAI',
      label: 'Bookings AI',
      parentKey: 'bookings',
      parentLabel: 'Bookings Management',
      description: 'AI-powered appointment scheduling, smart booking recommendations, and calendar optimisation.'
    },
    {
      key: 'communicationsAI',
      label: 'Communications AI',
      parentKey: 'communications',
      parentLabel: 'Communications',
      description: 'AI-generated campaign content, smart audience segmentation, and send-time optimisation.'
    },
    {
      key: 'loyaltyAI',
      label: 'Loyalty AI',
      parentKey: 'promotions',
      parentLabel: 'Promotions',
      description: 'AI-driven loyalty reward suggestions and personalised promotional offers for customers.'
    },
    {
      key: 'reportsAI',
      label: 'Reports AI',
      parentKey: 'reports',
      parentLabel: 'Reports',
      description: 'AI-generated business insights, trend analysis, and natural language report queries.'
    }
  ];

  const handleAISubModuleToggle = (subModuleKey, parentKey) => {
    if (!modules[parentKey]) return; // guard: parent must be enabled
    setModules(prev => ({ ...prev, [subModuleKey]: !prev[subModuleKey] }));
  };

  const handleSaveModules = async () => {
    try {
      setSaving(true);
      setError('');
      
      await updateClientModules(clientId, modules);
      setSuccessMessage('Modules updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating modules:', err);
      setError('Failed to update modules: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="config-page-container">
        <div className="loading-spinner">Loading client configuration...</div>
      </div>
    );
  }

  if (!clientData) {
    return (
      <div className="config-page-container">
        <div className="error-message">Client not found</div>
        <button onClick={handleBackToClients} className="back-button">
          Back to Clients
        </button>
      </div>
    );
  }

  return (
    <div className="config-page-container">
      <div className="config-page-header">
        <div className="config-header-left">
          <button onClick={handleBackToClients} className="back-button">
            ← Back to Clients
          </button>
          <h1>{clientData.name} - Configuration</h1>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <div className="config-tabs">
        <button
          className={`config-tab ${activeTab === 'modules' ? 'active' : ''}`}
          onClick={() => setActiveTab('modules')}
        >
          Modules
        </button>
        <button
          className={`config-tab ${activeTab === 'admins' ? 'active' : ''}`}
          onClick={() => setActiveTab('admins')}
        >
          Admin Users
        </button>
        <button
          className={`config-tab ${activeTab === 'urls' ? 'active' : ''}`}
          onClick={() => setActiveTab('urls')}
        >
          Customer and Admin URLs
        </button>
      </div>

      <div className="config-content">
        {activeTab === 'modules' && (
          <div className="modules-tab-content">
            <div className="tab-header">
              <h2>Enabled Modules</h2>
              <button
                onClick={handleSaveModules}
                className="save-modules-button"
                disabled={saving || loadingModules}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>

            {loadingModules ? (
              <div className="loading-spinner">Loading modules...</div>
            ) : (
              <div className="modules-list-container">
                <p className="modules-description">
                  Select which modules are enabled for this client. The Home module is always enabled.
                </p>
                
                <div className="modules-checkbox-list">
                  <div className="module-checkbox-item">
                    <label className="module-checkbox-label">
                      <input
                        type="checkbox"
                        checked={modules.home}
                        disabled={true}
                        className="module-checkbox-input"
                      />
                      <span className="module-name">Home</span>
                      <span className="module-tag always-enabled">Always Enabled</span>
                    </label>
                    <p className="module-description-text">
                      Core salon management features including branding, services, staff, and customer database.
                    </p>
                  </div>

                  <div className="module-checkbox-item">
                    <label className="module-checkbox-label">
                      <input
                        type="checkbox"
                        checked={modules.promotions}
                        onChange={() => handleModuleToggle('promotions')}
                        className="module-checkbox-input"
                      />
                      <span className="module-name">Promotions</span>
                    </label>
                    <p className="module-description-text">
                      Create and manage promotional campaigns, voucher codes, and special offers.
                    </p>
                  </div>

                  <div className="module-checkbox-item">
                    <label className="module-checkbox-label">
                      <input
                        type="checkbox"
                        checked={modules.communications}
                        onChange={() => handleModuleToggle('communications')}
                        className="module-checkbox-input"
                      />
                      <span className="module-name">Communications</span>
                    </label>
                    <p className="module-description-text">
                      Send email newsletters and push notifications to customers with segmentation.
                    </p>
                  </div>

                  <div className="module-checkbox-item">
                    <label className="module-checkbox-label">
                      <input
                        type="checkbox"
                        checked={modules.bookings}
                        onChange={() => handleModuleToggle('bookings')}
                        className="module-checkbox-input"
                      />
                      <span className="module-name">Bookings</span>
                    </label>
                    <p className="module-description-text">
                      Manage appointments, calendar scheduling, and online booking portal.
                    </p>
                  </div>

                  <div className="module-checkbox-item">
                    <label className="module-checkbox-label">
                      <input
                        type="checkbox"
                        checked={modules.reports}
                        onChange={() => handleModuleToggle('reports')}
                        className="module-checkbox-input"
                      />
                      <span className="module-name">Reports</span>
                    </label>
                    <p className="module-description-text">
                      View analytics, generate reports, and track performance metrics.
                    </p>
                  </div>

                  <div className="module-checkbox-item">
                    <label className="module-checkbox-label">
                      <input
                        type="checkbox"
                        checked={modules.bespokeai}
                        onChange={() => handleModuleToggle('bespokeai')}
                        className="module-checkbox-input"
                      />
                      <span className="module-name">BespokeAI</span>
                      <span className="module-tag premium">Premium</span>
                    </label>
                    <p className="module-description-text">
                      AI-powered recommendations, automation, and intelligent insights for all operations.
                    </p>

                    {modules.bespokeai && (
                      <div className="ai-submodules-section">
                        <p className="ai-submodules-heading">Select AI modules to enable:</p>
                        {AI_SUBMODULE_CONFIG.map(({ key, label, parentKey, parentLabel, description }) => {
                          const parentEnabled = modules[parentKey];
                          return (
                            <div
                              key={key}
                              className={`ai-submodule-item${!parentEnabled ? ' ai-submodule-disabled' : ''}`}
                            >
                              <label className="module-checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={modules[key]}
                                  onChange={() => handleAISubModuleToggle(key, parentKey)}
                                  disabled={!parentEnabled}
                                  className="module-checkbox-input"
                                />
                                <span className="module-name ai-submodule-name">{label}</span>
                                {!parentEnabled && (
                                  <span className="module-tag ai-locked">Requires {parentLabel}</span>
                                )}
                              </label>
                              <p className="module-description-text" style={{ marginLeft: '32px' }}>
                                {description}
                              </p>
                              {!parentEnabled && (
                                <p className="ai-submodule-notice">
                                  Enable the <strong>{parentLabel}</strong> module above before activating {label}.
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="modules-summary">
                  <h3>Modules Summary</h3>
                  <p>
                    <strong>Enabled Modules:</strong>{' '}
                    {['home', 'promotions', 'communications', 'bookings', 'reports', 'bespokeai'].filter(k => modules[k]).length} of 6
                  </p>
                  {modules.bespokeai && (
                    <p style={{ marginTop: '8px' }}>
                      <strong>AI Sub-modules Enabled:</strong>{' '}
                      {['bookingsAI', 'communicationsAI', 'loyaltyAI', 'reportsAI'].filter(k => modules[k]).length} of 4
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'admins' && (
          <div className="admins-tab-content">
            <div className="tab-header">
              <h2>Admin Users</h2>
              <button
                onClick={() => setShowAddAdminModal(true)}
                className="edit-urls-button"
                disabled={saving}
              >
                + Add Admin
              </button>
            </div>

            <table className="admins-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Firebase UID</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clientData.admins && clientData.admins.length > 0 ? (
                  clientData.admins.map((admin) => (
                    <tr key={admin.uid}>
                      <td>{admin.name}</td>
                      <td>{admin.email}</td>
                      <td><code style={{fontSize: '11px'}}>{admin.uid}</code></td>
                      <td>
                        <span className="role-badge-config">
                          {admin.role}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <button
                          onClick={() => {
                            setSelectedAdmin(admin);
                            setShowEditAdminModal(true);
                          }}
                          className="edit-admin-button"
                          disabled={saving}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteAdmin(admin.uid, admin.name)}
                          className="delete-admin-button"
                          disabled={saving}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="empty-cell">
                      No admin users found. Add an admin to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'urls' && (
          <div className="urls-tab-content">
            <div className="tab-header">
              <h2>Customer and Admin URLs</h2>
            </div>

            <div className="url-warning-notice">
              <h4>⚠️ Important Notice</h4>
              <p>
                <strong>Editing URLs after setup is not recommended</strong> as the URL is tied to the salonId.
                Changing URLs without updating the salonId will prevent admin and customer login.
              </p>
              <p>
                If you need to change the salon's URL, a migration script must be used to update the related values
                in the <code>clients</code>, <code>salons</code>, <code>salonUsers</code>, and <code>customers</code>
                collections in Firebase.
              </p>
            </div>

            <div className="urls-form">
              <div className="url-field">
                <label>Client Domain:</label>
                <div className="url-display">{clientData.clientDomain || 'Not configured'}</div>
              </div>

              <div className="url-field">
                <label>Admin Login URL:</label>
                <div className="url-display">{clientData.adminLoginURL || 'Not configured'}</div>
              </div>

              <div className="url-field">
                <label>Customer Login URL:</label>
                <div className="url-display">{clientData.customerLoginURL || 'Not configured'}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showAddAdminModal && (
        <AdminModal
          title="Add New Admin User"
          onClose={() => setShowAddAdminModal(false)}
          onSubmit={handleAddAdmin}
          loading={saving}
          enabledModules={modules}
          isEdit={false}
        />
      )}

      {showEditAdminModal && selectedAdmin && (
        <AdminModal
          title="Edit Admin User"
          initialData={selectedAdmin}
          onClose={() => {
            setShowEditAdminModal(false);
            setSelectedAdmin(null);
          }}
          onSubmit={handleEditAdmin}
          loading={saving}
          enabledModules={modules}
          isEdit={true}
        />
      )}
    </div>
  );
}

function AdminModal({ title, initialData, onClose, onSubmit, loading, enabledModules, isEdit }) {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    email: initialData?.email || '',
    firebaseUID: initialData?.uid || '',
    role: initialData?.role || 'admin',
    staffId: initialData?.staffId || ''
  });

  const handleRoleChange = (e) => {
    const newRole = e.target.value;
    setFormData(prev => ({
      ...prev,
      role: newRole,
      permissions: DEFAULT_PERMISSIONS[newRole] || DEFAULT_PERMISSIONS.staff
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Include default permissions for the selected role
    const submitData = {
      ...formData,
      permissions: formData.permissions || DEFAULT_PERMISSIONS[formData.role] || DEFAULT_PERMISSIONS.staff
    };
    onSubmit(submitData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content-config">
        <div className="modal-header">
          <h3>{title}</h3>
          <button onClick={onClose} className="modal-close-button">×</button>
        </div>

        <form onSubmit={handleSubmit} className="admin-form">
          {!isEdit && (
            <div className="manual-setup-notice">
              <h4>⚠️ Manual Setup Required</h4>
              <p><strong>Before adding this admin user, you must:</strong></p>
              <ol>
                <li>Go to <a href="https://console.firebase.google.com" target="_blank" rel="noopener noreferrer">Firebase Console</a></li>
                <li>Navigate to <strong>Authentication → Users</strong></li>
                <li>Click <strong>"Add User"</strong></li>
                <li>Enter the email and password for this admin</li>
                <li>Copy the <strong>UID</strong> generated by Firebase</li>
                <li>Paste the UID into the field below</li>
              </ol>
            </div>
          )}

          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="Admin full name"
            />
          </div>

          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              placeholder="admin@salon.com"
              disabled={isEdit}
            />
            {isEdit && (
              <p className="form-helper-text">
                📌 Email cannot be changed after creation
              </p>
            )}
          </div>

          {!isEdit && (
            <div className="form-group">
              <label>Firebase UID *</label>
              <input
                type="text"
                value={formData.firebaseUID}
                onChange={(e) => setFormData({ ...formData, firebaseUID: e.target.value })}
                required
                placeholder="Paste the UID from Firebase Console"
              />
              <p className="form-helper-text">
                ℹ️ This is the UID you copied from Firebase Authentication after creating the user
              </p>
            </div>
          )}

          {isEdit && (
            <div className="form-group">
              <label>Firebase UID</label>
              <input
                type="text"
                value={formData.firebaseUID}
                disabled
                className="disabled-field"
              />
              <p className="form-helper-text">
                📌 Firebase UID cannot be changed
              </p>
            </div>
          )}

          <div className="form-group">
            <label>Role</label>
            <select value={formData.role} onChange={handleRoleChange}>
              <option value="owner">Owner</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="staff">Staff</option>
            </select>
            <p className="form-helper-text" style={{ marginTop: '4px' }}>
              {formData.role === 'owner' && 'Full access to all features.'}
              {formData.role === 'admin' && 'Full access except User Management.'}
              {formData.role === 'manager' && 'Access to Bookings, Reports & Customers.'}
              {formData.role === 'staff' && 'Leave portal access only — can apply for own leave.'}
            </p>
          </div>

          {formData.role === 'staff' && (
            <div className="form-group">
              <label>Link to staffId (Mandatory)</label>
              <input
                type="text"
                value={formData.staffId}
                onChange={(e) => setFormData({ ...formData, staffId: e.target.value })}
                placeholder="staffId from the staff collection"
                required
              />
              <p className="form-helper-text">
                Link this login account to a bookable staff profile so their approved leave appears in the calendar.
              </p>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel-button">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="submit-button">
              {loading ? 'Saving...' : isEdit ? 'Update Admin' : 'Add Admin'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ClientConfigurationPage;